// BlogPostPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';

const BlogPostPage = () => {
  const { postSlug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    // Mock blog post data moved inside useEffect
    const blogPostsData = {
      "future-of-private-equity-trends-shaping-2024": {
        id: 1,
        slug: "future-of-private-equity-trends-shaping-2024",
        title: "The Future of Private Equity: Trends Shaping 2024",
        excerpt: "As we navigate through 2024, private equity firms are adapting to new market realities.",
        category: "market-trends",
        date: "March 15, 2024",
        readTime: "8 min read",
        author: "Sarah Johnson",
        authorTitle: "Managing Partner",
        authorBio: "Sarah is a Managing Partner at Morphis with over 15 years of experience in private equity and investment banking.",
        authorImage: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400",
        image: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1200",
        content: `
          <p>The private equity landscape is undergoing a fundamental transformation. As we navigate through 2024, firms are adapting to new market realities that demand greater operational expertise, sector specialization, and creative deal structuring.</p>
          
          <h2>The Rise of Operational Value Creation</h2>
          <p>Gone are the days when financial engineering alone could drive returns. Today's successful PE firms are building in-house operating partners who work alongside portfolio company management teams to drive operational improvements. This shift has accelerated as valuation multiples remain elevated and leverage becomes more expensive.</p>
          
          <p>We're seeing firms invest heavily in digital transformation, supply chain optimization, and commercial excellence programs. The most sophisticated firms are developing playbooks that can be deployed within the first 100 days of ownership.</p>
          
          <h2>Technology Integration Accelerates</h2>
          <p>Artificial intelligence and data analytics are no longer nice-to-haves, they're competitive necessities. Forward-thinking firms are using AI to screen deals, automate due diligence, and monitor portfolio company performance in real-time.</p>
          
          <p>Machine learning algorithms can now analyze thousands of potential acquisition targets, flagging those that match specific investment criteria. During due diligence, AI tools can review contracts, identify risks, and even predict future performance based on historical data patterns.</p>
          
          <h2>ESG: From Compliance to Value Creation</h2>
          <p>Environmental, Social, and Governance factors have moved from the sidelines to center stage. What was once seen as a compliance burden is now recognized as a value creation lever. Firms that ignore ESG are missing opportunities to reduce costs, mitigate risks, and build more resilient portfolio companies.</p>
          
          <p>Forward-thinking GPs are embedding ESG considerations throughout the investment lifecycle from deal sourcing through to exit. This includes everything from energy efficiency audits to diversity metrics and governance improvements.</p>
          
          <h2>What This Means for Investors</h2>
          <p>For institutional investors and family offices, these trends underscore the importance of selecting GPs with genuine operational capabilities, not just financial engineering skills. Look for firms that have invested in their own infrastructure, built sector expertise, and can demonstrate successful value creation across market cycles.</p>
          
          <p>The firms that thrive in this environment will be those that combine financial discipline with operational excellence, technological sophistication, and a genuine commitment to sustainable value creation.</p>
        `,
        tags: ["Private Equity", "Market Trends", "Investment Strategy"]
      },
      "esg-integration-beyond-compliance-to-value-creation": {
        id: 2,
        slug: "esg-integration-beyond-compliance-to-value-creation",
        title: "ESG Integration: Beyond Compliance to Value Creation",
        excerpt: "Environmental, Social, and Governance factors are no longer just compliance metrics.",
        category: "insights",
        date: "March 10, 2024",
        readTime: "6 min read",
        author: "Michael Chen",
        authorTitle: "ESG Director",
        authorBio: "Michael leads Morphis's ESG practice, helping portfolio companies integrate sustainable practices that drive value.",
        authorImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400",
        image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1200",
        content: `
          <p>ESG has evolved from a reporting requirement to a strategic imperative. Leading private equity firms now recognize that environmental, social, and governance factors are directly linked to financial performance and long-term value creation.</p>
          
          <h2>The Business Case for ESG</h2>
          <p>Recent studies show that companies with strong ESG performance outperform their peers across multiple metrics—lower cost of capital, higher valuations, and greater operational efficiency. For portfolio companies, this translates into real competitive advantages.</p>
          
          <h2>Practical Implementation</h2>
          <p>We work with portfolio companies to identify ESG opportunities that align with their business models. For a manufacturing firm, this might mean energy efficiency upgrades that reduce costs. For a technology company, it could involve diversity initiatives that drive innovation.</p>
          
          <p>The key is moving beyond checklist compliance to genuine integration—where ESG considerations inform strategic decisions at every level of the organization.</p>
        `,
        tags: ["ESG", "Sustainability", "Value Creation"]
      },
      "navigating-exits-in-a-volatile-market": {
        id: 3,
        slug: "navigating-exits-in-a-volatile-market",
        title: "Navigating Exits in a Volatile Market",
        excerpt: "Exit planning has become increasingly complex. Our experts share strategies for maximizing value.",
        category: "thought-leadership",
        date: "March 5, 2024",
        readTime: "10 min read",
        author: "David Williams",
        authorTitle: "Head of M&A",
        authorBio: "David has led over 50 successful transactions across technology, healthcare, and industrial sectors.",
        authorImage: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400",
        image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200",
        content: `
          <p>In today's uncertain market environment, exit planning requires more strategic thinking than ever before. Traditional approaches that worked in a rising tide may no longer deliver optimal results.</p>
          
          <h2>The Dual-Track Approach</h2>
          <p>More sellers are pursuing dual-track processes—simultaneously preparing for both an IPO and strategic sale. This approach creates optionality and often drives better outcomes by introducing competitive tension.</p>
          
          <h2>Timing Considerations</h2>
          <p>While it's tempting to wait for perfect market conditions, history shows that trying to time the market rarely works. Instead, focus on what you can control: operational performance and strategic positioning.</p>
          
          <p>The best time to sell is when your business is performing well, regardless of broader market conditions. Buyers will pay a premium for quality assets even in challenging environments.</p>
          
          <h2>Preparation is Everything</h2>
          <p>Companies that exit successfully typically begin preparation 12-24 months in advance. This includes cleaning up financials, strengthening management teams, and identifying potential deal risks before they become problems.</p>
          
          <p>Our experience shows that thorough preparation can increase exit valuations by 20-30% compared to rushed processes.</p>
        `,
        tags: ["Exit Strategy", "M&A", "Valuation"]
      }
    };

    // Simulate API call
    setTimeout(() => {
      const postData = blogPostsData[postSlug];
      if (postData) {
        setPost(postData);
        // Get related posts (same category, different slug)
        const related = Object.values(blogPostsData)
          .filter(p => p.slug !== postData.slug && p.category === postData.category)
          .slice(0, 2);
        setRelatedPosts(related);
      }
      setLoading(false);
    }, 500);
  }, [postSlug]); // Only depends on postSlug
  // Handle share functionality with actual URLs
  const handleShare = (platform) => {
    const url = window.location.href;
    const text = post?.title || 'Check out this article';
    
    let shareUrl = '';
    switch(platform) {
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="blog-post-loading">
        <div className="museum-wrap">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-post-not-found">
        <div className="museum-wrap">
          <h2>Article Not Found</h2>
          <p>The article you're looking for doesn't exist or has been moved.</p>
          <Link to="/blogs" className="back-to-blog-btn">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb />
      <div className="blog-post-page">
        <article>
          {/* Hero Section */}
          <div className="post-hero">
            <div className="museum-wrap">
              <div className="post-category-label">{post.category}</div>
              <h1>{post.title}</h1>
              <div className="post-meta">
                <div className="post-author-info">
                  <img src={post.authorImage} alt={post.author} className="author-avatar" />
                  <div>
                    <span className="author-name">{post.author}</span>
                    <span className="author-title-meta">{post.authorTitle}</span>
                  </div>
                </div>
                <div className="post-meta-details">
                  <span>{post.date}</span>
                  <span>•</span>
                  <span>{post.readTime}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          <div className="post-featured-image">
            <img src={post.image} alt={post.title} />
          </div>

          {/* Content */}
          <div className="post-content-wrapper">
            <div className="museum-wrap">
              <div className="post-content-grid">
                <div className="post-content"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />

                <aside className="post-sidebar">
                  <div className="author-card">
                    <img src={post.authorImage} alt={post.author} />
                    <h4>{post.author}</h4>
                    <p className="author-title-sidebar">{post.authorTitle}</p>
                    <p className="author-bio">{post.authorBio}</p>
                  </div>

                  <div className="tags-card">
                    <h4>Topics</h4>
                    <div className="tags-list">
                      {post.tags.map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                  </div>

                  <div className="share-card">
                    <h4>Share this article</h4>
                    <div className="share-links">
                      <button onClick={() => handleShare('linkedin')} className="share-link">
                        LinkedIn
                      </button>
                      <button onClick={() => handleShare('twitter')} className="share-link">
                        Twitter
                      </button>
                      <button onClick={() => handleShare('email')} className="share-link">
                        Email
                      </button>
                    </div>
                  </div>
                </aside>
              </div>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div className="related-posts">
                  <h3>Related Articles</h3>
                  <div className="related-posts-grid">
                    {relatedPosts.map(related => (
                      <Link to={`/blogs/${related.slug}`} key={related.slug} className="related-post-card">
                        <img src={related.image} alt={related.title} />
                        <div className="related-post-content">
                          <span className="related-category">{related.category}</span>
                          <h4>{related.title}</h4>
                          <p>{related.excerpt.substring(0, 100)}...</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>
      </div>

      <style>{`
        .blog-post-page {
          background: var(--bg-main, #fffdf6);
          min-height: 100vh;
        }

        /* Hero Section */
        .post-hero {
          padding-top: 88px;
          background: var(--bg-main, #fffdf6);
          border-bottom: 1px solid var(--border-light, #e6e0d4);
        }

        .back-to-blog {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 13px;
          color: var(--text-muted, #6b7280);
          text-decoration: none;
          margin-bottom: 24px;
          transition: color 0.2s;
        }

        .back-to-blog:hover {
          color: var(--color-orange, #E87722);
        }

        .post-category-label {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--color-orange, #E87722);
          margin-bottom: 16px;
        }

        .post-hero h1 {
          font-family: var(--font-heading, 'IBM Plex Serif', serif);
          font-size: clamp(2rem, 5vw, 3.5rem);
          line-height: 1.2;
          margin-bottom: 24px;
          color: var(--color-dark, #1a1a1a);
          max-width: 900px;
        }

        .post-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 40px;
        }

        .post-author-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .author-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          object-fit: cover;
        }

        .author-name {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 14px;
          font-weight: 400;
          color: var(--color-dark, #1a1a1a);
          display: block;
        }

        .author-title-meta {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 11px;
          color: var(--text-muted, #6b7280);
        }

        .post-meta-details {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 12px;
          color: var(--text-muted, #6b7280);
          display: flex;
          gap: 8px;
        }

        /* Featured Image */
        .post-featured-image {
          width: 100%;
          max-height: 500px;
          overflow: hidden;
        }

        .post-featured-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        /* Content */
        .post-content-wrapper {
          padding: 60px 0 80px;
        }

        .post-content-grid {
          display: grid;
          grid-template-columns: 1fr 300px;
          gap: 60px;
        }

        .post-content {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 1.05rem;
          line-height: 1.8;
          color: var(--text-sub, #2d2d2d);
        }

        .post-content h2 {
          font-family: var(--font-heading, 'IBM Plex Serif', serif);
          font-size: 1.6rem;
          margin: 32px 0 16px;
          color: var(--color-dark, #1a1a1a);
        }

        .post-content p {
          margin-bottom: 20px;
        }

        .post-content ul, .post-content ol {
          margin: 20px 0;
          padding-left: 28px;
        }

        .post-content li {
          margin-bottom: 8px;
        }

        /* Sidebar */
        .post-sidebar {
          position: sticky;
          top: 100px;
        }

        .author-card {
          border: 1px solid var(--border-light, #e6e0d4);
          padding: 24px;
          text-align: center;
          margin-bottom: 32px;
        }

        .author-card img {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          object-fit: cover;
          margin-bottom: 16px;
        }

        .author-card h4 {
          font-family: var(--font-heading, 'IBM Plex Serif', serif);
          font-size: 1.1rem;
          margin-bottom: 4px;
        }

        .author-title-sidebar {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 11px;
          font-weight: 400;
          color: var(--color-orange, #E87722);
          margin-bottom: 12px;
        }

        .author-bio {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 0.8rem;
          line-height: 1.5;
          color: var(--text-muted, #6b7280);
        }

        .tags-card, .share-card {
          border: 1px solid var(--border-light, #e6e0d4);
          padding: 24px;
          margin-bottom: 32px;
        }

        .tags-card h4, .share-card h4 {
          font-family: var(--font-heading, 'IBM Plex Serif', serif);
          font-size: 1rem;
          margin-bottom: 16px;
        }

        .tags-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .tag {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 11px;
          padding: 4px 12px;
          background: #ffe9c34f;
          color: var(--color-orange, #E87722);
          border-radius: 20px;
        }

        .share-links {
          display: flex;
          gap: 16px;
        }

        .share-link {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 12px;
          color: var(--text-muted, #6b7280);
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          transition: color 0.2s;
        }

        .share-link:hover {
          color: var(--color-orange, #E87722);
        }

        /* Related Posts */
        .related-posts {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 1px solid var(--border-light, #e6e0d4);
        }

        .related-posts h3 {
          font-family: var(--font-heading, 'IBM Plex Serif', serif);
          font-size: 1.5rem;
          margin-bottom: 32px;
        }

        .related-posts-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 32px;
        }

        .related-post-card {
          display: flex;
          gap: 20px;
          text-decoration: none;
          color: inherit;
          border: 1px solid var(--border-light, #e6e0d4);
          padding: 16px;
          transition: all 0.3s;
        }

        .related-post-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
        }

        .related-post-card img {
          width: 100px;
          height: 100px;
          object-fit: cover;
        }

        .related-post-content {
          flex: 1;
        }

        .related-category {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 9px;
          font-weight: 400;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--color-orange, #E87722);
        }

        .related-post-content h4 {
          font-family: var(--font-heading, 'IBM Plex Serif', serif);
          font-size: 1rem;
          margin: 8px 0;
          line-height: 1.4;
        }

        .related-post-content p {
          font-family: var(--font-body, Arial, sans-serif);
          font-size: 0.8rem;
          color: var(--text-muted, #6b7280);
        }

        /* Loading */
        .blog-post-loading {
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 3px solid var(--border-light);
          border-top-color: var(--color-orange);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Not Found */
        .blog-post-not-found {
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .back-to-blog-btn {
          display: inline-block;
          padding: 12px 28px;
          background: var(--color-orange);
          color: #fff;
          text-decoration: none;
          margin-top: 20px;
        }

        /* Responsive */
        @media (max-width: 992px) {
          .post-content-grid {
            grid-template-columns: 1fr;
          }

          .post-sidebar {
            position: static;
          }

          .related-posts-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .post-hero {
            padding-top: 68px;
          }

          .post-meta {
            flex-direction: column;
            align-items: flex-start;
          }

          .related-post-card {
            flex-direction: column;
          }

          .related-post-card img {
            width: 100%;
            height: 150px;
          }
        }
      `}</style>
    </>
  );
};

export default BlogPostPage;